import { ChatMessage } from '../stores/useChatStore.js';

export interface CIStatusEvent {
  id: string;
  type: 'ci_started' | 'ci_success' | 'ci_failure' | 'ci_cancelled';
  repository: string;
  branch: string;
  commit: string;
  workflow: string;
  duration?: string;
  url?: string;
  timestamp: Date;
}

export interface PRStatusEvent {
  id: string;
  number: number;
  title: string;
  status: 'merged' | 'failed' | 'open' | 'pending';
  repository: string;
  branch: string;
  url: string;
  timestamp: Date;
  relatedTaskId?: string;
}

export interface TelemetryEvent {
  id: string;
  type: 'ci_status' | 'deployment' | 'error' | 'performance';
  source: string;
  data: Record<string, unknown>;
  timestamp: Date;
}

class ARCOSTelemetry {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8000';
  }

  async processCIStatusEvent(event: CIStatusEvent): Promise<ChatMessage> {
    const getStatusEmoji = (type: CIStatusEvent['type']) => {
      switch (type) {
        case 'ci_success': return '✅';
        case 'ci_failure': return '❌';
        case 'ci_started': return '🔄';
        case 'ci_cancelled': return '⏹️';
        default: return '📊';
      }
    };

    const getStatusText = (type: CIStatusEvent['type']) => {
      switch (type) {
        case 'ci_success': return 'passed';
        case 'ci_failure': return 'failed';
        case 'ci_started': return 'started';
        case 'ci_cancelled': return 'cancelled';
        default: return 'updated';
      }
    };

    const emoji = getStatusEmoji(event.type);
    const statusText = getStatusText(event.type);
    const durationText = event.duration ? ` in ${event.duration}` : '';

    return {
      id: `ci_${event.id}`,
      sender: 'bee',
      content: `${emoji} ${event.workflow} ${statusText}${durationText}`,
      type: 'text',
      timestamp: event.timestamp instanceof Date ? event.timestamp.toISOString() : event.timestamp,
      buildCredits: 15 // BC (Build Credits)
    };
  }

  async injectCIStatus(
    repository: string,
    branch: string,
    status: 'success' | 'failure' | 'started' | 'cancelled',
    workflow: string = 'CI/CD Pipeline',
    duration?: string
  ): Promise<ChatMessage> {
    const event: CIStatusEvent = {
      id: `ci_${Date.now()}`,
      type: `ci_${status}` as CIStatusEvent['type'],
      repository,
      branch,
      commit: `${Math.random().toString(36).substr(2, 7)}`,
      workflow,
      duration,
      url: `https://github.com/${repository}/actions`,
      timestamp: new Date()
    };

    return this.processCIStatusEvent(event);
  }

  simulateCIEvents(onEvent: (message: ChatMessage) => void): void {
    const events = [
      { status: 'started' as const, delay: 2000, duration: undefined },
      { status: 'success' as const, delay: 8000, duration: '2m 34s' },
      { status: 'failure' as const, delay: 15000, duration: '1m 45s' }
    ];

    events.forEach(({ status, delay, duration }) => {
      setTimeout(async () => {
        const message = await this.injectCIStatus(
          'DDPMLS/echoctl-website',
          'beehive/chatshell-agent-workspace',
          status,
          'Beehive CI Pipeline',
          duration
        );
        onEvent(message);
      }, delay);
    });
  }

  async getRecentPRs(_sessionId: string): Promise<PRStatusEvent[]> {
    return [];
    
    // return [
    //   {
    //     id: `pr_${Date.now()}`,
    //     number: 39,
    //     title: 'UX Pack – Monaco shell',
    //     status: 'open',
    //     repository: 'DDPMLS/echoctl-website',
    //     branch: 'beehive/chatshell-agent-workspace',
    //     url: 'https://github.com/DDPMLS/echoctl-website/pull/39',
    //     timestamp: new Date(),
    //     relatedTaskId: sessionId
    //   },
    //   {
    //     id: `pr_${Date.now() + 1}`,
    //     number: 26,
    //     title: 'UX Pack – Monaco editor shell',
    //     status: 'merged',
    //     repository: 'DDPMLS/echoctl-website',
    //     branch: 'feat/ux-monaco-clean',
    //     url: 'https://github.com/DDPMLS/echoctl-website/pull/26',
    //     timestamp: new Date(Date.now() - 86400000),
    //     relatedTaskId: sessionId
    //   }
    // ];
  }

  async logTelemetryEvent(event: TelemetryEvent): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/api/telemetry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_ARCOS_API_KEY || 'dev-key'}`
        },
        body: JSON.stringify(event)
      });
    } catch (error) {
      console.error('Failed to log telemetry event:', error);
    }
  }
}

export const arcTelemetry = new ARCOSTelemetry();
export const arcTelemetryService = arcTelemetry;
